import React, { useEffect, useState } from "react";
import { getDatabase, ref, child, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc as firestoreDoc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { FaPrint } from "react-icons/fa";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadReport from "../components/DownloadReport";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const realtimeDB = getDatabase();
  const firestore = getFirestore();
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [rawEmployees, setRawEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [filterId, setFilterId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [sortStatus, setSortStatus] = useState("All");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  const [empForm, setEmpForm] = useState({
    empId: "",
    name: "",
    joinedDate: "",
    password: "",
  });

  // ---------- AUTH CHECK ----------
  useEffect(() => {
    const JSKMID = localStorage.getItem("AdminID");
    const Password = localStorage.getItem("Password");
    const role = localStorage.getItem("role");

    // Require an admin role and credentials to access this page
    if (!JSKMID || !Password || role !== "Admin") {
      navigate("/login");
    }
  }, [navigate]);

  // NEW: which employee rows are expanded (shows activity list). Array of empId strings.
  const [expandedIds, setExpandedIds] = useState([]);

  const formattedDateKey = (d = new Date()) =>
    d.toLocaleDateString("en-GB").replace(/\//g, "_");

  /* ----------------- helpers (unchanged) ----------------- */
  const parseMaybeRange = (value) => {
    if (!value || typeof value !== "string")
      return { start: null, end: null, duration: null };
    const parts = value.split("-");
    if (parts.length === 3)
      return {
        start: parts[0].trim(),
        end: parts[1].trim(),
        duration: parts[2].trim(),
      };
    if (parts.length === 1)
      return { start: parts[0].trim(), end: null, duration: null };
    return {
      start: parts[0].trim(),
      end: parts[1] ? parts[1].trim() : null,
      duration: parts[2] ? parts[2].trim() : null,
    };
  };
  const isActivityKey = (key, value) => {
    if (!key || typeof key !== "string") return false;
    const loweredKey = key.toLowerCase();

    // Exact matches only for exclusion
    const excludedFields = [
      "name",
      "logintime",
      "productionstarted",
      "breakstotal",
    ];

    if (excludedFields.includes(loweredKey)) return false;

    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();

    // pattern for times like "9:43:22 AM" or "09:43:22 PM"
    const singleTimePattern = /^\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?$/i;

    // range pattern detection (existing behavior)
    if (trimmed.includes("-")) {
      const timePattern = /(\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?)/gi;
      const matches = trimmed.match(timePattern);
      return matches && matches.length >= 2;
    }

    // accept single time strings as activities (so an active break shows up)
    if (singleTimePattern.test(trimmed)) {
      return true;
    }

    return false;
  };

  const prettifyActivityKey = (key) => {
    if (!key) return "Activity";
    const lowered = key.toLowerCase();
    const knownTypes = {
      lunchbreak: "Lunch Break",
      doubt: "Doubt Session",
      break: "Break",
      restroom: "Restroom",
      awayfromdesk: "Away From Desk",
      teabreak: "Tea Break",
      coffeebreak: "Coffee Break",
      productionstarted: "Production",
    };
    for (const [k, v] of Object.entries(knownTypes)) {
      if (lowered.includes(k)) {
        const numberMatch = key.match(/(\d+)$/);
        return numberMatch ? `${v} ${numberMatch[1]}` : v;
      }
    }
    const numberMatch = key.match(/(\d+)$/);
    const baseName = key.replace(/(\d+)$/, "").trim();
    if (baseName && baseName.length > 1) {
      const formattedBase = baseName
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
      return numberMatch ? `${formattedBase} ${numberMatch[1]}` : formattedBase;
    }
    return numberMatch
      ? `Custom Activity ${numberMatch[1]}`
      : "Custom Activity";
  };

  const computeDurationFromTimes = (startStr, endStr) => {
    try {
      const start = new Date(`1970/01/01 ${startStr}`);
      const end = new Date(`1970/01/01 ${endStr}`);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      const diffSec = Math.max(0, Math.floor((end - start) / 1000));
      const hrs = Math.floor(diffSec / 3600);
      const mins = Math.floor((diffSec % 3600) / 60);
      const secs = diffSec % 60;
      return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    } catch {
      return null;
    }
  };

  const parseEmployeeNode = (empId, raw) => {
    const result = {
      empId,
      name: raw.Name || raw.name || "",
      loginTime: raw.LoginTime || raw.logintime || null,
      production: {
        raw: raw.productionstarted || null,
        start: null,
        end: null,
        hours: null,
        active: false,
      },
      breaksTotal: raw.breakstotal || null,
      activities: [],
      otherFields: raw,
      onBreak: false,
      actualProduction: null, // <- new field to hold ActualProduction for THIS date
      weeklyActual: 0, // will be filled later
      monthlyActual: 0, // will be filled later
    };

    // --- capture ActualProduction (case-insensitive) if present ---
    const actualKey = Object.keys(raw || {}).find(
      (k) => k.toLowerCase() === "actualproduction",
    );
    if (actualKey) {
      result.actualProduction = raw[actualKey];
    }
    // -------------------------------------------------------------

    if (result.production.raw) {
      const parsed = parseMaybeRange(result.production.raw);
      result.production.start = parsed.start;
      result.production.end = parsed.end;
      result.production.hours = parsed.duration || null;
      result.production.active = !parsed.end;
    }

    Object.keys(raw).forEach((k) => {
      const value = raw[k];

      // skip explicitly excluded keys (case-insensitive check)
      const excluded = [
        "LoginTime",
        "Name",
        "productionstarted",
        "breakstotal",
        "LogoutTime",
      ];
      if (excluded.some((ex) => ex.toLowerCase() === k.toLowerCase())) return;

      // If the value is a string that looks like a single timestamp (no "-"),
      // consider this an active break and add to activities with start only.
      if (typeof value === "string") {
        const trimmed = value.trim();
        const singleTimePattern = /^\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?$/i;

        if (singleTimePattern.test(trimmed) && !trimmed.includes("-")) {
          // mark onBreak
          result.onBreak = true;

          // push an activity so it shows up in the activity list
          result.activities.push({
            key: k,
            label: prettifyActivityKey(k),
            rawKey: k,
            start: trimmed,
            end: null,
            duration: null,
            raw: value,
          });

          // continue to next key (we already handled it)
          return;
        }
      }

      // existing logic: detect full ranges (or activities recognized by isActivityKey)
      if (isActivityKey(k, value)) {
        const parsed = parseMaybeRange(value);
        result.activities.push({
          key: k,
          label: prettifyActivityKey(k),
          rawKey: k,
          start: parsed.start,
          end: parsed.end,
          duration: parsed.duration,
          raw: value,
        });
      }
    });

    // ... rest unchanged ...
    result.activities.sort((a, b) => {
      if (!a.start || !b.start) return a.start ? -1 : 1;
      const tA = new Date(`1970/01/01 ${a.start}`).getTime();
      const tB = new Date(`1970/01/01 ${b.start}`).getTime();
      return tA - tB;
    });

    if (
      !result.production.hours &&
      result.production.start &&
      result.production.end
    ) {
      result.production.hours = computeDurationFromTimes(
        result.production.start,
        result.production.end,
      );
    }

    return result;
  };

  /* ----------------- fetch (store raw then filter locally) ----------------- */

  // helper: convert Date -> date key string 'dd_mm_yyyy'
  const keyFromDate = (d) => formattedDateKey(d);

  // returns array of date keys for Monday..Sunday that include the anchor date
  const getWeekDateKeys = (anchor = new Date()) => {
    // JS getDay: 0=Sun,1=Mon,...6=Sat
    const day = anchor.getDay();
    // compute monday date
    // If Sunday (0) we need to go back 6 days to get Monday
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(
      anchor.getFullYear(),
      anchor.getMonth(),
      anchor.getDate() + diffToMonday,
    );
    const keys = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(
        monday.getFullYear(),
        monday.getMonth(),
        monday.getDate() + i,
      );
      keys.push(keyFromDate(d));
    }
    return keys;
  };

  // returns array of date keys for 1st..last day of the anchor's month
  const getMonthDateKeys = (anchor = new Date()) => {
    const year = anchor.getFullYear();
    const month = anchor.getMonth(); // 0..11
    const last = new Date(year, month + 1, 0); // day 0 of next month is last day of this month
    const daysInMonth = last.getDate();
    const keys = [];
    for (let d = 1; d <= daysInMonth; d++) {
      keys.push(keyFromDate(new Date(year, month, d)));
    }
    return keys;
  };

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    setEmployees([]); // clear visible list while loading
    try {
      // --- 1) fetch the selected-date node (used to populate the table for chosen date)
      const dateKey = formattedDateKey(date);
      const dbRef = ref(realtimeDB);
      const snap = await get(child(dbRef, `${dateKey}`));
      if (!snap.exists()) {
        setRawEmployees([]);
        setEmployees([]);
        setLoading(false);
        return;
      }

      const raw = snap.val();
      const parsed = Object.entries(raw).map(([empId, node]) =>
        parseEmployeeNode(empId, node || {}),
      );

      // --- 2) AGGREGATE actual production for week (Mon..Sun) and month (1st..last) using the currently selected date as anchor
      const anchorDate = date; // use selected date so week/month reflect date picker
      const weekKeys = getWeekDateKeys(anchorDate); // array of 7 keys
      const monthKeys = getMonthDateKeys(anchorDate); // 1st..last of month
      const allKeys = Array.from(new Set([...weekKeys, ...monthKeys])); // dedupe

      // fetch all required date nodes in parallel
      const dateFetchPromises = allKeys.map(async (key) => {
        try {
          const s = await get(child(dbRef, key));
          return { key, val: s.exists() ? s.val() : null };
        } catch (e) {
          console.warn("Failed to fetch date node", key, e);
          return { key, val: null };
        }
      });

      const dateNodes = await Promise.all(dateFetchPromises);

      // compute membership sets for quick checks
      const weekSet = new Set(weekKeys);
      const monthSet = new Set(monthKeys);

      // build a map: empId -> { week: number, month: number }
      const agg = {}; // { [empId]: { week: number, month: number } }
      dateNodes.forEach(({ key, val }) => {
        if (!val) return;
        const isWeek = weekSet.has(key);
        const isMonth = monthSet.has(key);
        // val is object with employee IDs as keys
        Object.entries(val).forEach(([empId, empNode]) => {
          if (!empNode) return;
          // find ActualProduction key case-insensitive
          const actualKey = Object.keys(empNode).find(
            (k) => k.toLowerCase() === "actualproduction",
          );
          if (!actualKey) return;
          let rawVal = empNode[actualKey];
          if (rawVal === null || rawVal === undefined || rawVal === "") return;
          // try parse number; accept strings like "1.20" or "1,20"
          if (typeof rawVal === "string")
            rawVal = rawVal.replace(",", ".").trim();
          const num = parseFloat(rawVal);
          if (isNaN(num)) return;

          if (!agg[empId]) agg[empId] = { week: 0, month: 0 };
          if (isWeek) agg[empId].week += num;
          if (isMonth) agg[empId].month += num;
        });
      });

      // attach weekly/monthly sums to parsed employees (and default 0 if missing)
      parsed.forEach((p) => {
        const a = agg[p.empId];
        p.weeklyActual = a ? Number(a.week) : 0;
        p.monthlyActual = a ? Number(a.month) : 0;
      });

      // sort once
      parsed.sort((a, b) => {
        if (a.loginTime && b.loginTime) {
          const tA = new Date(`1970/01/01 ${a.loginTime}`).getTime();
          const tB = new Date(`1970/01/01 ${b.loginTime}`).getTime();
          return tA - tB;
        }
        if (a.name && b.name) return a.name.localeCompare(b.name);
        return a.empId.localeCompare(b.empId);
      });

      setRawEmployees(parsed);
      applyFilters(parsed, filterId, filterName); // apply current filters immediately
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(err.message || "Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (
    source = rawEmployees,
    idFilterRaw = filterId,
    nameFilterRaw = filterName,
    statusFilter = sortStatus,
  ) => {
    const idFilter = (idFilterRaw || "").trim().toLowerCase();
    const nameFilter = (nameFilterRaw || "").trim().toLowerCase();

    let filtered = source;
    if (idFilter) {
      filtered = filtered.filter((e) =>
        (e.empId || "").toLowerCase().includes(idFilter),
      );
    }
    if (nameFilter) {
      filtered = filtered.filter((e) =>
        (e.name || "").toLowerCase().includes(nameFilter),
      );
    }

    if (statusFilter === "All") {
      setEmployees(filtered);
      return;
    }

    const matchesStatus = (e) => {
      if (statusFilter === "Active") return e.production.active && !e.onBreak;
      if (statusFilter === "On Break") return e.onBreak;
      if (statusFilter === "Inactive")
        return !e.production.active && !e.onBreak;
      return false;
    };

    const matched = [];
    const others = [];
    for (const e of filtered) {
      if (matchesStatus(e)) matched.push(e);
      else others.push(e);
    }

    setEmployees([...matched, ...others]);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, filterName, rawEmployees, sortStatus]);

  // when date changes, fetch new data (fetchEmployees already applies filters)
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  /* ---------- Form helpers / add employee (unchanged) ---------- */
  const resetForm = () => {
    setEmpForm({ empId: "", name: "", joinedDate: "", password: "" });
    setFormErrors({});
    setSuccessMessage("");
  };

  const validateForm = () => {
    const errs = {};
    const empId = (empForm.empId || "").trim();
    const name = (empForm.name || "").trim();
    const joinedDate = empForm.joinedDate;
    const password = (empForm.password || "").trim();

    if (!empId) errs.empId = "Employee ID is required";
    else if (!/^[A-Za-z0-9-_]+$/.test(empId))
      errs.empId = "Employee ID can contain letters, numbers, - or _ only";

    if (!name) errs.name = "Name is required";
    if (!joinedDate) errs.joinedDate = "Joined date is required";
    if (!password) errs.password = "Password is required";
    else if (password.length < 6)
      errs.password = "Password must be at least 6 characters";

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const formatJoinedDateForStore = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${Number(d)}/${Number(m)}/${Number(y)}`;
  };

  const handleAddEmployeeClick = () => {
    resetForm();
    setShowAddForm(true);
  };

  const handleCancelAdd = () => {
    resetForm();
    setShowAddForm(false);
  };

  const handleConfirmEmployee = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setAdding(true);
    setFormErrors({});
    setError("");
    setSuccessMessage("");
    const empIdRaw = empForm.empId.trim();
    const empId = empIdRaw.toUpperCase();
    const name = empForm.name.trim();
    const joinedDate = formatJoinedDateForStore(empForm.joinedDate);
    const password = empForm.password.trim();

    try {
      const docRef = firestoreDoc(firestore, "Employee_IDS", empId);
      const existing = await getDoc(docRef);
      if (existing.exists()) {
        setFormErrors({ empId: "An employee with this ID already exists" });
        setAdding(false);
        return;
      }

      const payload = {
        JSKMID: empId,
        "Joined Date": joinedDate,
        Name: name,
        Password: password,
      };
      await setDoc(docRef, payload);

      setSuccessMessage("Employee data added successfully");
      setTimeout(async () => {
        setShowAddForm(false);
        resetForm();
        await fetchEmployees();
        setAdding(false);
        setSuccessMessage("");
      }, 1200);
    } catch (err) {
      console.error("Error adding employee:", err);
      setError("Failed to add employee. Try again.");
      setAdding(false);
    }
  };

  const clearFilters = () => {
    setFilterId("");
    setFilterName("");
    setSortStatus("");
    applyFilters(rawEmployees, "", "");
  };

  // NEW: toggle expand for an empId
  const toggleExpand = (empId) => {
    setExpandedIds((prev) => {
      const exists = prev.includes(empId);
      if (exists) return prev.filter((id) => id !== empId);
      return [...prev, empId];
    });
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      navigate("/login");
      window.location.reload();
    } catch (err) {
      console.error("Error during handleLogout:", err);
    }
  };

  const handleConfirmLogout = async () => {
    await handleLogout();
  };

  const getStatusLabel = (emp) => {
    if (emp.onBreak) return "On Break";
    if (emp.production.active) return "Active";
    return "Inactive";
  };

  const handlePrintEmployee = (emp) => {
    const doc = new jsPDF();
    const marginLeft = 15;
    let currentY = 18;

    const logo = "/logo.png";

    const addBackground = () => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // set faint opacity so content is visible on top
      const g = doc.GState ? new doc.GState({ opacity: 0.15 }) : null;
      if (g) doc.setGState(g);

      const logoWidth = pageWidth * 0.8; // adjust for size
      const logoHeight = logoWidth * 0.5; // adjust based on logo aspect ratio

      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;

      // --- Draw logo once rotated 45° (diagonal)
      doc.addImage(
        logo,
        "PNG",
        centerX - logoWidth / 6,
        centerY - logoHeight / 5,
        logoWidth,
        logoHeight,
        undefined,
        "NONE",
        45,
        "center",
      );

      // Reset opacity to normal for other content
      doc.setGState(new doc.GState({ opacity: 1 }));
    };

    // ---- Add watermark on page 1 ----
    addBackground();

    const statusText = getStatusLabel(emp);

    // ---- Header ----
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Employee Daily Report", 105, currentY, { align: "center" });
    currentY += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("JSKM Technologies Private Limited", 105, currentY, {
      align: "center",
    });
    currentY += 8;

    doc.text(`Date: ${date.toDateString()}`, 105, currentY, {
      align: "center",
    });
    currentY += 12;

    // ---- Basic Info ----
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Employee Details", marginLeft, currentY);
    currentY += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    const basicRows = [
      ["Employee ID", emp.empId || "-"],
      ["Name", emp.name || "-"],
      ["Login Time", emp.loginTime || "-"],
      ["Breaks Total", emp.breaksTotal || "-"],
      ["Production Start", emp.production.start || "-"],
      ["Production End", emp.production.end || "-"],
      ["Working Hours", emp.production.hours || "-"],
      ["Actual Production (today)", emp.actualProduction ?? "-"],
      [
        "Actual Production (week)",
        typeof emp.weeklyActual === "number"
          ? emp.weeklyActual.toFixed(2)
          : "-",
      ],
      [
        "Actual Production (month)",
        typeof emp.monthlyActual === "number"
          ? emp.monthlyActual.toFixed(2)
          : "-",
      ],
      ["Status", statusText],
    ];

    basicRows.forEach(([label, value]) => {
      doc.text(`${label}:`, marginLeft, currentY);
      doc.text(String(value), marginLeft + 50, currentY);
      currentY += 7;
    });

    currentY += 6;

    // ---- Activities ----
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Activities", marginLeft, currentY);
    currentY += 6;

    const activityRows =
      emp.activities && emp.activities.length > 0
        ? emp.activities.map((a) => [
            a.label || "-",
            a.start || "-",
            a.end || "-",
            a.duration || "-",
          ])
        : [["No activities recorded", "-", "-", "-"]];

    autoTable(doc, {
      startY: currentY,
      head: [["Activity", "Start", "End", "Duration"]],
      body: activityRows,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [232, 113, 21] },
      margin: { left: marginLeft, right: marginLeft },

      // 🔥 This runs for *every* page that autoTable creates
      didDrawPage: () => {
        addBackground();
      },
    });

    const dateKey = dateToInputValue(date);
    doc.save(`${emp.empId || "Employee"}_${dateKey}.pdf`);
  };

  /* ---------- UI ---------- */
  return (
    <div style={{ padding: 20, fontFamily: "sans-serif", minHeight: "80vh" }}>
      {/* Header is hidden when the add form is shown */}
      {!showAddForm && (
        <header
          style={{
            padding: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1 style={{ color: "#e87115" }}>Admin Dashboard</h1>
            <div style={{ color: "#666", marginTop: "10px" }}>
              Realtime Employee Activity — {date.toDateString()}
            </div>
            <div style={{ color: "#666", marginTop: "10px" }}>
              JSKM Technologies Private Limited
            </div>
          </div>

          {/* Right controls: Add Employee above date/refresh and filters */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: "flex-end",
            }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button
                onClick={handleAddEmployeeClick}
                style={{
                  background: "#e87115",
                  color: "#fff",
                  border: "none",
                  padding: "8px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                  boxShadow: "0 2px 6px rgba(232,113,21,0.12)",
                }}
              >
                + Add Employee
              </button>

              <button
                onClick={() => setShowLogoutConfirm(true)}
                style={{
                  background: "#e87115",
                  color: "#fff",
                  border: "none",
                  padding: "8px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                  boxShadow: "0 2px 6px rgba(232,113,21,0.12)",
                }}
              >
                Logout
              </button>
            </div>

            {/* Filters row: Date, Employee ID, Name, Refresh, Clear */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => setShowDownload(true)}
                style={{
                  background: "#e87115",
                  color: "#fff",
                  border: "none",
                  padding: "8px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Download
              </button>
              <input
                type="date"
                value={dateToInputValue(date)}
                onChange={(e) => setDate(inputValueToDate(e.target.value))}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                }}
              />

              <input
                type="text"
                placeholder="Filter by Employee ID"
                value={filterId}
                onChange={(e) => setFilterId(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  minWidth: 160,
                }}
              />

              <input
                type="text"
                placeholder="Filter by Name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  minWidth: 200,
                }}
              />

              <button
                onClick={fetchEmployees}
                style={{
                  background: "#e87115",
                  color: "#fff",
                  border: "none",
                  padding: "8px 14px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Refresh
              </button>

              <button
                onClick={clearFilters}
                style={{
                  background: "#fff",
                  color: "#333",
                  border: "1px solid #ddd",
                  padding: "8px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
              {/* Sort By Dropdown */}
              <select
                value={sortStatus}
                onChange={(e) => setSortStatus(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  minWidth: 180,
                  color: "#333",
                }}
              >
                <option value="All">Sort By: All (Default)</option>
                <option value="Active">Active</option>
                <option value="On Break">On Break</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </header>
      )}

      <main style={{ marginTop: 18 }}>
        {showAddForm ? (
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 60,
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              width: "90%",
              maxWidth: "900px",
              margin: "24px auto",
              boxSizing: "border-box",
            }}
          >
            <h2 style={{ marginTop: 0, color: "#e87115", textAlign: "center" }}>
              Add Employee
            </h2>

            <form
              onSubmit={handleConfirmEmployee}
              style={{ display: "grid", gap: 12, width: "100%" }}
            >
              <label
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#666464ff",
                  }}
                >
                  Create Employee ID
                </span>
                <input
                  value={empForm.empId}
                  onChange={(e) =>
                    setEmpForm((p) => ({ ...p, empId: e.target.value }))
                  }
                  placeholder="e.g. JSKMID004"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${formErrors.empId ? "#e11d48" : "#ddd"}`,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                {formErrors.empId && (
                  <div style={{ color: "#e11d48" }}>{formErrors.empId}</div>
                )}
              </label>

              <label
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#666464ff",
                  }}
                >
                  Name
                </span>
                <input
                  value={empForm.name}
                  onChange={(e) =>
                    setEmpForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Full name"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${formErrors.name ? "#e11d48" : "#ddd"}`,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                {formErrors.name && (
                  <div style={{ color: "#e11d48" }}>{formErrors.name}</div>
                )}
              </label>

              <div style={{ display: "flex", gap: 12 }}>
                <label
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#666464ff",
                    }}
                  >
                    Date of Joining
                  </span>
                  <input
                    type="date"
                    value={empForm.joinedDate}
                    onChange={(e) =>
                      setEmpForm((p) => ({ ...p, joinedDate: e.target.value }))
                    }
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${formErrors.joinedDate ? "#e11d48" : "#ddd"}`,
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  />
                  {formErrors.joinedDate && (
                    <div style={{ color: "#e11d48" }}>
                      {formErrors.joinedDate}
                    </div>
                  )}
                </label>

                <label
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#666464ff",
                    }}
                  >
                    Password
                  </span>
                  <input
                    value={empForm.password}
                    onChange={(e) =>
                      setEmpForm((p) => ({ ...p, password: e.target.value }))
                    }
                    placeholder="Choose a secure password"
                    type="password"
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${formErrors.password ? "#e11d48" : "#ddd"}`,
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  />
                  {formErrors.password && (
                    <div style={{ color: "#e11d48" }}>
                      {formErrors.password}
                    </div>
                  )}
                </label>
              </div>

              {error && <div style={{ color: "red" }}>{error}</div>}
              {successMessage && (
                <div style={{ color: "#059669" }}>{successMessage}</div>
              )}

              <div
                style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  onClick={handleCancelAdd}
                  style={{
                    background: "#fff",
                    border: "1px solid #ddd",
                    padding: "8px 14px",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                  disabled={adding}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    background: "#e87115",
                    color: "#fff",
                    border: "none",
                    padding: "8px 14px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                  disabled={adding}
                >
                  {adding ? "Adding…" : "Confirm Employee"}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "60vh",
                }}
              >
                Loading...
              </div>
            ) : error ? (
              <div style={{ color: "red" }}>{error}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: 10,
                  }}
                >
                  <thead
                    style={{
                      background: "#fff6f0",
                      borderBottom: `3px solid #e87115`,
                    }}
                  >
                    <tr>
                      <th style={thStyle}>S NO.</th>
                      <th style={thStyle}>Employee ID</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Login Time</th>
                      <th style={thStyle}>Activities</th>
                      <th style={thStyle}>Breaks Total</th>
                      <th style={thStyle}>Production Start</th>
                      <th style={thStyle}>Production End</th>
                      <th style={thStyle}>Working Hours</th>
                      <th style={thStyle}>Actual Production</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Print</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{
                            padding: 18,
                            textAlign: "center",
                            color: "#666",
                          }}
                        >
                          No employee records for this date
                        </td>
                      </tr>
                    ) : (
                      employees.map((emp, index) => {
                        const count = emp.activities.length;
                        const isExpanded = expandedIds.includes(emp.empId);
                        return (
                          <React.Fragment key={emp.empId}>
                            <tr
                              style={{
                                borderBottom: "1px solid #e87115",
                                backgroundColor: "#fff6f0",
                              }}
                            >
                              <td style={tdStyle}>{index + 1}</td>
                              <td style={tdStyle}>{emp.empId}</td>
                              {/* UPDATED: show weekly/monthly beside name */}
                              <td style={tdStyle}>
                                {emp.name || "-"}{" "}
                                <span
                                  style={{ fontWeight: 700, color: "#e87115" }}
                                >
                                  (
                                  {typeof emp.weeklyActual === "number"
                                    ? emp.weeklyActual.toFixed(2)
                                    : "0.00"}
                                  /
                                  {typeof emp.monthlyActual === "number"
                                    ? emp.monthlyActual.toFixed(2)
                                    : "0.00"}
                                  )
                                </span>
                              </td>
                              <td style={tdStyle}>{emp.loginTime || "-"}</td>
                              <td style={tdStyle}>
                                {count === 0 ? (
                                  "-"
                                ) : (
                                  // clickable badge/button shows count and toggles activity details below
                                  <button
                                    onClick={() => toggleExpand(emp.empId)}
                                    aria-expanded={isExpanded}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "6px 10px",
                                      borderRadius: 20,
                                      border: "1px solid #e87115",
                                      background: isExpanded
                                        ? "#fff"
                                        : "#e87115",
                                      color: isExpanded ? "#e87115" : "#fff",
                                      fontWeight: 700,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {count}
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        opacity: 0.9,
                                      }}
                                    >
                                      activity{count > 1 ? "s" : ""}
                                    </span>
                                  </button>
                                )}
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  color: "#e87115",
                                  fontWeight: 700,
                                }}
                              >
                                {emp.breaksTotal || "-"}
                              </td>
                              <td style={tdStyle}>
                                {emp.production.start || "-"}
                              </td>
                              <td style={tdStyle}>
                                {emp.production.end || "-"}
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  color: "#e87115",
                                  fontWeight: 700,
                                }}
                              >
                                {emp.production.hours || "-"}
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  color: "#e87115",
                                  fontWeight: 700,
                                }}
                              >
                                {emp.actualProduction ?? "-"}
                              </td>
                              <td style={{ ...tdStyle }}>
                                {emp.onBreak ? (
                                  <span
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: 20,
                                      background: "#fff4d8",
                                      color: "#ee0f0f",
                                      border: "1px solid #ea250f",
                                      fontWeight: 700,
                                      display: "inline-block",
                                      animation: "pulseGlow 1.5s infinite",
                                    }}
                                  >
                                    On Break
                                  </span>
                                ) : emp.production.active ? (
                                  <span
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: 20,
                                      background: "#e6fff2",
                                      color: "#0a8a4c",
                                      border: "1px solid #c7f4db",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Active
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: 20,
                                      background: "#fff9f0",
                                      color: "#e87115",
                                      border: "1px solid #f3cebb",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Inactive
                                  </span>
                                )}

                                <style>
                                  {`
                                    @keyframes pulseGlow {
                                    0% { box-shadow: 0 0 0 0 rgba(238, 15, 15, 0.5); }
                                    70% { box-shadow: 0 0 0 10px rgba(238, 15, 15, 0); }
                                    100% { box-shadow: 0 0 0 0 rgba(238, 15, 15, 0); }
                                  }
                                `}
                                </style>
                              </td>
                              <td style={tdStyle}>
                                <button
                                  onClick={() => handlePrintEmployee(emp)}
                                  title="Print Employee Report"
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #e87115",
                                    borderRadius: 6,
                                    padding: "6px 8px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                                  }}
                                >
                                  <FaPrint
                                    style={{ fontSize: 14, color: "#e87115" }}
                                  />
                                </button>
                              </td>
                            </tr>

                            {/* NEW: activity details row shown when expanded */}
                            {isExpanded && (
                              <tr
                                key={`${emp.empId}-activities`}
                                style={{
                                  backgroundColor: "#fff",
                                  borderBottom: "1px solid #f3e0d6",
                                }}
                              >
                                <td colSpan={12} style={{ padding: 12 }}>
                                  {emp.activities.length === 0 ? (
                                    <div style={{ color: "#666" }}>
                                      No activities recorded
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                      }}
                                    >
                                      {emp.activities.map((activity) => {
                                        const isActiveBreak =
                                          !!activity.start && !activity.end;

                                        const baseStyle = {
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          gap: 12,
                                          padding: "8px 10px",
                                          borderRadius: 8,
                                          background: "#fff6f8",
                                          border: "1px solid #fee9e0",
                                        };

                                        const activeOverride = {
                                          background: "#fff7e6",
                                          border:
                                            "1px solid rgba(232,113,21,0.9)",
                                          boxShadow:
                                            "0 6px 18px rgba(232,113,21,0.06)",
                                          transform: "translateY(-1px)",
                                        };

                                        const timeRowStyle = {
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-end",
                                          gap: 8,
                                          color: isActiveBreak
                                            ? "#a63600"
                                            : "#444",
                                          fontSize: 15,
                                          whiteSpace: "nowrap",
                                          minWidth: 220,
                                        };

                                        const labelColor = isActiveBreak
                                          ? "#b94a00"
                                          : "#333";

                                        const startText = activity.start
                                          ? activity.start
                                          : "-";
                                        const endText = activity.end
                                          ? activity.end
                                          : null;
                                        const durationText = activity.duration
                                          ? ` (${activity.duration})`
                                          : "";

                                        return (
                                          <div
                                            key={activity.key}
                                            style={{
                                              ...baseStyle,
                                              ...(isActiveBreak
                                                ? activeOverride
                                                : {}),
                                            }}
                                            aria-live={
                                              isActiveBreak
                                                ? "polite"
                                                : undefined
                                            }
                                            title={
                                              isActiveBreak
                                                ? "Active break in progress"
                                                : undefined
                                            }
                                          >
                                            {/* left: activity label */}
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontWeight: 700,
                                                  color: labelColor,
                                                }}
                                              >
                                                {activity.label}
                                              </div>

                                              {activity.rawKey !==
                                                activity.label
                                                  .toLowerCase()
                                                  .replace(/\s+/g, "") && (
                                                <div
                                                  style={{
                                                    fontSize: 12,
                                                    color: "#999",
                                                    fontStyle: "italic",
                                                  }}
                                                >
                                                  {activity.rawKey}
                                                </div>
                                              )}
                                            </div>

                                            <div style={timeRowStyle}>
                                              <div>{startText}</div>

                                              {endText && (
                                                <div>→ {endText}</div>
                                              )}

                                              {durationText && (
                                                <div>{durationText}</div>
                                              )}

                                              {isActiveBreak && (
                                                <span
                                                  style={{
                                                    display: "inline-block",
                                                    marginLeft: 8,
                                                    padding: "4px 8px",
                                                    borderRadius: 12,
                                                    background: "#fff1e6",
                                                    color: "#b94a00",
                                                    border:
                                                      "1px solid rgba(232,113,21,0.4)",
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    verticalAlign: "middle",
                                                    boxShadow:
                                                      "0 2px 8px rgba(232,113,21,0.06)",
                                                    whiteSpace: "nowrap",
                                                    animation:
                                                      "pulseGlow 1.5s infinite",
                                                  }}
                                                >
                                                  In progress
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {showLogoutConfirm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Confirm Logout</h3>
            <p style={{ marginTop: 10 }}>Are you sure you want to log out?</p>
            <div style={{ marginTop: 16 }}>
              <button
                onClick={handleConfirmLogout}
                style={{
                  ...styles.confirmBtn,
                  background: "#e11d48",
                  color: "#fff",
                }}
              >
                Yes, Logout
              </button>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  ...styles.confirmBtn,
                  background: "#e5e7eb",
                  color: "#111827",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDownload && (
        <DownloadReport onClose={() => setShowDownload(false)} />
      )}
    </div>
  );
};

/* small presentational helpers */
const thStyle = {
  textAlign: "left",
  padding: "12px 14px",
  color: "#374651",
  fontSize: 14,
  fontWeight: 700,
};
const tdStyle = { padding: "12px 14px", color: "#4b5563", fontSize: 14 };
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(6px)",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "24px 28px",
    minWidth: 380,
    boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
    textAlign: "center",
  },
  confirmBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    margin: "0 6px",
  },
};

const dateToInputValue = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const inputValueToDate = (v) => {
  if (!v) return new Date();
  const [y, m, d] = v.split("-");
  return new Date(Number(y), Number(m) - 1, Number(d));
};

export default AdminDashboard;
